import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTransition, animated } from "react-spring";

import {
  PageSection,
  TextHolder,
  PageSectionTitle,
  PageSectionHeader,
} from "../styled";
import { H3, H4, H5, H6, P, SmallP } from "shared/ui/headings";
import { Gap } from "shared/ui/gap";
import { colors } from "shared/ui/colors";
import { BlueprintIcon } from "shared/ui/blueprint-icon";
import { BUTTON_VARIANTS, FakeButton } from "shared/ui/button";
import ImageLoader from "shared/ui/image-loader";
import Multimedia from "shared/ui/blocks/multimedia";
import { Arrows, Selectors } from "../../../../shared/ui/slideshow";
import { useInView } from "react-intersection-observer";
import { useTranslationsArray } from "../../../../shared/context/translations";
import { useSiteAreaContext } from "../../../../shared/context/site-area";
import Link from "../../../../../vicoustic/client/commons/link";

const Feedback = (props, ref) => {
  const [active, setActive] = useState(0);
  const [inViewRef, inView] = useInView({
    treshold: 0.05,
  });
  const { siteArea } = useSiteAreaContext()
  const { t, tA } = useTranslationsArray(`homepageSections.${siteArea}.feedback`)
  const transitions = useTransition(active, null, {
    from: {
      wait: 0,
      display: "none",
      transform: "translate3d(0,20px,0)",
      opacity: 0,
    },
    enter: [
      { wait: 1 },
      { display: "block", transform: "translate3d(0,0px,0)", opacity: 1 },
    ],
    leave: [
      { transform: "translate3d(0,20px,0)", opacity: 0 },
      { display: "none" },
    ],
  });

  const tout = useRef(null);
  useEffect(() => {
    if (inView) {
      tout.current = setTimeout(
        () => setActive((prev) => (prev + 1 + slides.length) % slides.length),
        8000
      );
    }
    return () => clearTimeout(tout.current);
  }, [active, inView]);
  const slides = tA('items')

  if (slides.length === 0) return null
  return (
    <PageSection $siteArea={siteArea} ref={ref} $invertColors $noPadding>
      <PageSectionHeader>
        <PageSectionTitle>{t('name')}</PageSectionTitle>
        <BlueprintIcon color={colors.white} />
      </PageSectionHeader>
      <Multimedia ref={inViewRef} $align={"stretch"}>
        <div
          style={{
            position: "relative",
            zIndex: 0,
            maxHeight: "750px",
            height: '100%',
            overflow: "hidden",
          }}
        >
          {transitions.map(({ item, props, key }) => (
            <animated.div
              style={{
                ...props,
                position: "absolute",
                width: "100%",
                zIndex: -1,
                height: '100%',
                top: 0,
                left: 0,
              }}
              key={key}
            >
              <ImageLoader
                source={slides[item].image?.src || ''}
                imgProps={{ style: { width: "100%", height: '100%', objectFit: 'cover', maxWidth: "auto" } }}
              />
            </animated.div>
          ))}
          <ImageLoader
            viewports={{
              xs:
                "/images/content/hp/applications-slideshow/base-resized-1920.png",
            }}
            viewportsRatio={{
              xs: "1920:1080",
            }}
            imgProps={{ style: { width: "100%", maxWidth: "auto", opacity: 0 } }}
          />
        </div>
        <>
          {transitions.map(({ item, props, key }) => (
            <TextHolder key={key} as={animated.div} style={{ ...props, alignSelf: 'flex-end', paddingTop: '120px' }}>
              <Gap />
              <H3 $bold><i>{slides[item].title}</i></H3>
              <Gap />
              {slides[item].description && <>
                <H5><i style={{ lineHeight: '130%' }}>{slides[item].description}</i></H5>
                <Gap />
              </>}
              <SmallP>{slides[item].source}</SmallP>
              <Gap />
              <Link to={slides[item].button.link}>
                <FakeButton $withHover variant={BUTTON_VARIANTS.FILLED}>
                  {slides[item].button.text}
                </FakeButton>
              </Link>
            </TextHolder>
          ))}
          <Selectors
            itemCount={slides.length}
            setActive={setActive}
            active={active}
          />
          <Gap />
        </>
      </Multimedia>
      <Arrows setActive={setActive} itemCount={slides.length} />
    </PageSection>
  );
};

export default forwardRef(Feedback);
